import type { SocialRevision, UpdateSocialContentBody } from '@api/models';
import { useInfiniteQuery, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  type SocialContentFilters,
  deleteSocialContent,
  getSocialContent,
  getSocialFileUrl,
  listSocialContents,
  updateSocialContent
} from '@website/services/social.service';

const SOCIAL_KEY = ['social-contents'] as const;
// The API's links to show a file last an hour: refetch well before they expire.
const LINK_STALE_MS = 50 * 60_000;
const LIST_KEY = [...SOCIAL_KEY, 'list'] as const;
const oneKey = (id: string) => [...SOCIAL_KEY, 'one', id] as const;

export function useSocialContents(filters: SocialContentFilters) {
  return useInfiniteQuery({
    queryKey: [...LIST_KEY, filters],
    queryFn: ({ pageParam }) => listSocialContents(filters, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.cursor ?? undefined,
    staleTime: LINK_STALE_MS,
    refetchInterval: LINK_STALE_MS
  });
}

export function useSocialContent(id: string) {
  return useQuery({
    queryKey: oneKey(id),
    queryFn: () => getSocialContent(id),
    retry: false,
    staleTime: 30_000
  });
}

/** Links to show a revision's files, in order (1 hour each); `refetch` renews one that failed. */
export function useSocialFileLinks(contentId: string, revision: SocialRevision | undefined) {
  const files = revision?.files ?? [];
  return useQueries({
    queries: files.map((file) => ({
      queryKey: [...SOCIAL_KEY, 'file', contentId, revision?.id, file.key],
      queryFn: async () => (await getSocialFileUrl(contentId, revision!.id, file.key)).url,
      staleTime: LINK_STALE_MS,
      refetchOnWindowFocus: false
    }))
  });
}

export function useUpdateSocialContent(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateSocialContentBody) => updateSocialContent(id, body),
    // Never the file links: a reload would flash the images and restart the video.
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: LIST_KEY }),
        queryClient.invalidateQueries({ queryKey: oneKey(id) })
      ])
  });
}

export function useDeleteSocialContent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteSocialContent(id),
    onSuccess: (_data, id) => {
      queryClient.removeQueries({ queryKey: oneKey(id) });
      return queryClient.invalidateQueries({ queryKey: LIST_KEY });
    }
  });
}

/** Saves a file: a fresh link with an attachment disposition, opened in place. */
export function useDownloadSocialFile(contentId: string) {
  return useMutation({
    mutationFn: async ({ revisionId, key }: { revisionId: string; key: string }) => {
      const { url } = await getSocialFileUrl(contentId, revisionId, key, true);
      window.location.assign(url);
    }
  });
}
